import React, { useCallback, useMemo, useState } from 'react';
import AsyncSelect from 'react-select/async';
import Layout from '../Layout';
import { useLocation } from 'react-router-dom';
import {
  createPackage,
  generateWebsiteV2JourneyDraft,
  updatePackage,
} from '../../api/Packages';
import { loadAirportLocations, loadCityLocations } from '../../api/Location';
import { usePermissions, useRole } from '../../hooks/usePermissions';
import {
  calculatePackageStartingPrice,
  getCabMaxOccupancy,
  normalizePackageCabPricing,
  normalizePackageFaq,
  normalizePackagePlaces,
  normalizeStringArray,
  PACKAGE_CAB_TYPES,
} from '../../utils/packageTemplate';
import {
  PackageAiGeneratedField,
  PackageCabPricingRow,
  PackageCabType,
  TravelPackage,
} from '../../types/TravelPackage';
import { LocationOption } from '../../types/Location';
import { Hotel, TravellerInfo } from '../../types/Quotation';
import { getDatePlusDays } from '../../utils/date';
import HotelListModal from '../QuotationBuilder/HotelListModal';
import {
  isPackageBuilderLocationState,
  PackageBuilderLocationState,
} from './contracts';
import {
  normalizeItineraryDraftDay,
  type GuidedItineraryDay,
} from './guidedItinerary';
import '../../css/package-builder/sections.css';
import '../../css/package-builder/guided.css';

type BuilderPlace = {
  cityName: string;
  cityCode: string;
  nights: number;
};

type SelectedHotelRecord = {
  placeIndex: number;
  hotel: Hotel & {
    gstApplied?: boolean;
    gstBasePrice?: number;
    gstAmount?: number;
    gstRate?: number;
  };
};

type BuilderItineraryDay = GuidedItineraryDay;

type BuilderFaq = {
  question: string;
  answer: string;
};

type BuilderActivityPricingRow = {
  id: string;
  name: string;
  price: number;
};

type ReviewTagTone = 'default' | 'gold' | 'blue' | 'green' | 'red';

const AI_MANAGED_FIELDS: PackageAiGeneratedField[] = [
  'title',
  'summary',
  'durationLabel',
  'routeLabel',
  'destinationSlugs',
  'primaryDestinationSlug',
  'styleTags',
  'bestFor',
  'highlights',
  'itinerary',
  'includedExperiences',
  'stayStyle',
  'seo',
];

const PACKAGE_INCLUSIONS = [
  'Hotel',
  'Selected Meal',
  'Sightseeing & Activities',
  'Transfers',
  'Prime Guidance',
];

const PACKAGE_EXCLUSIONS = [
  'Entrance fees and activity charges excluded.',
  'Unspecified meals and refreshments are excluded.',
  'Personal expenses tips and porterage excluded.',
  'Early check-in and late checkout excluded.',
  'Seat selection and excess baggage excluded.',
  'Anything not listed under inclusions excluded.',
];

const EMPTY_PLACE: BuilderPlace = {
  cityName: '',
  cityCode: '',
  nights: 1,
};

const DEFAULT_HOTEL_TRAVELLERS: TravellerInfo = {
  adults: 2,
  children: 0,
  infants: 0,
};

const slugify = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const nextTempRowId = (() => {
  let sequence = 0;
  return () => `temp-row-${++sequence}`;
})();

const buildEmptyTransferRow = (cabType: PackageCabType = 'Sedan'): PackageCabPricingRow => ({
  tempId: nextTempRowId(),
  cabType,
  maxOccupancy: getCabMaxOccupancy(cabType),
  price: 0,
  isActive: true,
  isDeleted: false,
});

const getDisplayedTransferRows = (rows: PackageCabPricingRow[]) => rows.filter((row) => !row.isDeleted);

const buildTransferTableRows = (rows: PackageCabPricingRow[]): PackageCabPricingRow[] =>
  PACKAGE_CAB_TYPES.map((cabType) => {
    const existingRow = rows.find((row) => !row.isDeleted && row.cabType === cabType);
    return existingRow || buildEmptyTransferRow(cabType);
  });

const buildEmptyActivityPricingRow = (): BuilderActivityPricingRow => ({
  id: nextTempRowId(),
  name: '',
  price: 0,
});

export const normalizeJourneyDraftHighlights = (value: unknown): string[] => {
  const normalized = Array.isArray(value)
    ? normalizeStringArray(value)
    : typeof value === 'string'
      ? value
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean)
      : [];

  return Array.from(new Set(normalized));
};

export const normalizeReviewTagArray = (value: unknown): string[] => {
  const flattenValue = (item: unknown): string[] => {
    if (typeof item !== 'string') return [];
    const trimmed = item.trim();
    if (!trimmed) return [];
    const unescaped = trimmed.replace(/\\"/g, '"');

    const quotedTokens = Array.from(unescaped.matchAll(/"([^"]+)"/g))
      .map((match) => match[1].trim())
      .filter(Boolean);
    if (quotedTokens.length > 0) {
      return quotedTokens.flatMap(flattenValue);
    }

    const parsedJson = (() => {
      try {
        return JSON.parse(unescaped);
      } catch {
        return null;
      }
    })();

    if (Array.isArray(parsedJson)) {
      return parsedJson.flatMap(flattenValue);
    }
    if (typeof parsedJson === 'string') {
      return flattenValue(parsedJson);
    }

    return unescaped
      .split(',')
      .map((part) => part.trim())
      .map((part) => part
        .replace(/^[\s"'`{]+/, '')
        .replace(/^\[/, '')
        .replace(/[\s"'`}]+$/, '')
        .replace(/\]$/, '')
        .replace(/\\"/g, '"'))
      .filter(Boolean);
  };

  const normalized = Array.isArray(value)
    ? value.flatMap(flattenValue)
    : flattenValue(value);

  return Array.from(new Set(normalized));
};

const HOTEL_GST_THRESHOLD_PER_ROOM_PER_NIGHT = 7500;
const HOTEL_GST_LOW_RATE = 0.05;
const HOTEL_GST_HIGH_RATE = 0.18;

export const getHotelGstRate = (perRoomPerNight: number): number => (
  Number(perRoomPerNight || 0) > HOTEL_GST_THRESHOLD_PER_ROOM_PER_NIGHT
    ? HOTEL_GST_HIGH_RATE
    : HOTEL_GST_LOW_RATE
);

export const calculateHotelPriceWithGst = (basePrice: number, nights: number): {
  basePrice: number;
  gstAmount: number;
  gstRate: number;
  totalPrice: number;
} => {
  const safeBasePrice = Math.max(0, Number(basePrice || 0));
  const safeNights = Math.max(1, Number(nights || 1));
  const perRoomPerNight = safeBasePrice / safeNights;
  const gstRate = getHotelGstRate(perRoomPerNight);
  const gstAmount = Math.round(safeBasePrice * gstRate);
  return {
    basePrice: safeBasePrice,
    gstAmount,
    gstRate,
    totalPrice: safeBasePrice + gstAmount,
  };
};

const getHotelStoredPrice = (hotel: SelectedHotelRecord['hotel']) => {
  const explicitPrice = Number(hotel.price || hotel.display_price || 0);
  if (!hotel.gstApplied) return Math.max(0, explicitPrice);
  return Math.max(0, Number(hotel.price || hotel.display_price || 0));
};

const ReviewTagField: React.FC<{
  label: string;
  value: string[];
  onChange: (next: string[]) => void;
  placeholder: string;
  tone?: ReviewTagTone;
  transform?: (value: string) => string;
}> = ({ label, value, onChange, placeholder, tone = 'default', transform }) => {
  const [draft, setDraft] = useState('');

  const addTag = () => {
    const nextValue = transform ? transform(draft) : draft.trim();
    if (!nextValue) return;
    onChange(Array.from(new Set([...(value || []), nextValue])));
    setDraft('');
  };

  const removeTag = (index: number) => {
    onChange(value.filter((_, itemIndex) => itemIndex !== index));
  };

  return (
    <label className="guided-package-builder__review-field">
      <span>{label}</span>
      <div className="guided-package-builder__tag-field">
        <div className="guided-package-builder__tag-input-row">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addTag();
              }
            }}
            className="package-editor-input"
            placeholder={placeholder}
          />
          <button type="button" className="package-editor-btn-primary" onClick={addTag}>
            Add
          </button>
        </div>
        <div className="guided-package-builder__review-tags">
          {value.map((item, index) => (
            <span key={`${item}-${index}`} className={`guided-package-builder__review-tag guided-package-builder__review-tag--${tone}`}>
              {item}
              <button
                type="button"
                className="guided-package-builder__review-tag-remove"
                onClick={() => removeTag(index)}
                aria-label={`Delete ${label}`}
                title={`Delete ${label}`}
              >
                x
              </button>
            </span>
          ))}
        </div>
      </div>
    </label>
  );
};

const GuidedPackageBuilder: React.FC = () => {
  const location = useLocation();
  const locationState: PackageBuilderLocationState | undefined = isPackageBuilderLocationState(location.state)
    ? location.state
    : undefined;
  const permissions = usePermissions();
  const role = useRole();

  const packageToCopy: TravelPackage | undefined = locationState?.packageToCopy;
  const packageToEdit: TravelPackage | undefined = locationState?.packageToEdit;
  const sourcePackage = packageToEdit || packageToCopy;

  const [editingId, setEditingId] = useState<string | null>(packageToEdit?.id ? String(packageToEdit.id) : null);
  const [step, setStep] = useState(0);
  const [packageName, setPackageName] = useState(sourcePackage?.title || '');
  const [subtitle, setSubtitle] = useState(sourcePackage?.subtitle || '');
  const [description, setDescription] = useState(sourcePackage?.summary || sourcePackage?.description || '');
  const [durationLabel, setDurationLabel] = useState(
    sourcePackage?.durationLabel || sourcePackage?.duration_label || ''
  );
  const [routeLabel, setRouteLabel] = useState(sourcePackage?.routeLabel || sourcePackage?.route_label || '');
  const [destinationSlugs, setDestinationSlugs] = useState<string[]>(
    normalizeStringArray(
      sourcePackage?.destinationSlugs ||
      sourcePackage?.destination_slugs ||
      (sourcePackage as any)?.relatedDestinations ||
      (sourcePackage as any)?.related_destinations
    ).map(slugify)
  );
  const [primaryDestinationSlug, setPrimaryDestinationSlug] = useState(
    sourcePackage?.primaryDestinationSlug ||
    sourcePackage?.primary_destination_slug ||
    destinationSlugs[0] ||
    ''
  );
  const [bestFor, setBestFor] = useState<string[]>(
    normalizeReviewTagArray(sourcePackage?.bestFor || sourcePackage?.best_for || (sourcePackage as any)?.idealFor)
  );
  const [stayStyle, setStayStyle] = useState(sourcePackage?.stayStyle || sourcePackage?.stay_style || '');
  const [seoTitle, setSeoTitle] = useState(sourcePackage?.seo?.title || '');
  const [seoDescription, setSeoDescription] = useState(sourcePackage?.seo?.description || '');
  const [arrivalAt, setArrivalAt] = useState(sourcePackage?.arrivalAt || sourcePackage?.arrival_at || '');
  const [departureFrom, setDepartureFrom] = useState(sourcePackage?.departureFrom || sourcePackage?.departure_from || '');
  const [arrivalOption, setArrivalOption] = useState<LocationOption | null>(() => {
    const label = sourcePackage?.arrivalAt || sourcePackage?.arrival_at || '';
    return label ? { label, value: label, type: 'airport' } : null;
  });
  const [departureOption, setDepartureOption] = useState<LocationOption | null>(() => {
    const label = sourcePackage?.departureFrom || sourcePackage?.departure_from || '';
    return label ? { label, value: label, type: 'airport' } : null;
  });
  const [places, setPlaces] = useState<BuilderPlace[]>(() => {
    const normalized = normalizePackagePlaces(sourcePackage);
    return normalized.length > 0
      ? normalized.map((place) => ({
        cityName: place.cityName,
        cityCode: place.cityCode || '',
        nights: place.nights,
      }))
      : [{ ...EMPTY_PLACE }];
  });
  const [roughDraft, setRoughDraft] = useState('');
  const [highlights, setHighlights] = useState<string[]>(
    Array.from(new Set(
      normalizeJourneyDraftHighlights(sourcePackage?.highlights)
    ))
  );
  const [itinerary, setItinerary] = useState<BuilderItineraryDay[]>(() => {
    if (Array.isArray(sourcePackage?.itinerary) && sourcePackage!.itinerary.length > 0) {
      return sourcePackage!.itinerary.map((day) => ({
        day: Number(day.day || 0) || 1,
        title: String(day.title || '').trim(),
        points: normalizeStringArray(day.points),
        description: String((day as any).description || '').trim(),
      }));
    }
    return [];
  });
  const [faq, setFaq] = useState<BuilderFaq[]>(normalizePackageFaq(sourcePackage?.faq));
  const [searchTags, setSearchTags] = useState<string[]>(
    normalizeReviewTagArray(sourcePackage?.searchTags || sourcePackage?.search_tags)
  );
  const [dayPointDrafts, setDayPointDrafts] = useState<Record<number, string>>({});
  const [baseStartingPrice] = useState<number>(
    Number(sourcePackage?.baseStartingPrice ?? sourcePackage?.base_starting_price ?? 0) || 0
  );
  const [transferRows, setTransferRows] = useState<PackageCabPricingRow[]>(() => {
    const normalized = normalizePackageCabPricing(sourcePackage);
    return normalized.length > 0 ? normalized : [buildEmptyTransferRow('Sedan')];
  });
  const [heroImageFile, setHeroImageFile] = useState<File | null>(null);
  const [heroImagePreview, setHeroImagePreview] = useState(sourcePackage?.hero_image || '');
  const [galleryFiles, setGalleryFiles] = useState<File[]>([]);
  const [selectedHotels, setSelectedHotels] = useState<SelectedHotelRecord[]>(() => {
    const packageHotels = sourcePackage?.hotels;
    const hotels = Array.isArray(packageHotels) ? packageHotels : [];
    return hotels.map((hotel: any, index) => ({
      placeIndex: Number(hotel?.placeIndex ?? hotel?.place_index ?? index) || 0,
      hotel,
    }));
  });
  const [hotelModalPlaceIndex, setHotelModalPlaceIndex] = useState<number | null>(null);
  const [activityPricingRows, setActivityPricingRows] = useState<BuilderActivityPricingRow[]>(() => {
    const sourceActivities = Array.isArray((sourcePackage as any)?.activities) ? (sourcePackage as any).activities : [];
    return sourceActivities.length > 0
      ? sourceActivities.map((activity: any, index: number) => ({
        id: String(activity?.id || activity?.slug || `activity-${index + 1}`),
        name: String(activity?.name || '').trim(),
        price: Math.max(0, Number(activity?.price || activity?.quoteBasePrice || 0)),
      }))
      : [buildEmptyActivityPricingRow()];
  });
  const [visaPrice, setVisaPrice] = useState<number>(() => {
    const visaDetails = Array.isArray((sourcePackage as any)?.visaDetails)
      ? (sourcePackage as any).visaDetails
      : Array.isArray((sourcePackage as any)?.visa_details)
        ? (sourcePackage as any).visa_details
        : [];
    return visaDetails.reduce((sum: number, item: any) => sum + Math.max(0, Number(item?.price || 0)), 0);
  });
  const [insurancePrice, setInsurancePrice] = useState<number>(() => {
    const insuranceDetails = Array.isArray((sourcePackage as any)?.insuranceDetails)
      ? (sourcePackage as any).insuranceDetails
      : Array.isArray((sourcePackage as any)?.insurance_details)
        ? (sourcePackage as any).insurance_details
        : [];
    return insuranceDetails.reduce((sum: number, item: any) => sum + Math.max(0, Number(item?.price || 0)), 0);
  });
  const [priceOverride, setPriceOverride] = useState<number | ''>(() => {
    const override = Number(sourcePackage?.price?.base || sourcePackage?.price_breakdown?.total || 0);
    return Number.isFinite(override) && override > 0 ? override : '';
  });
  const [isPublished, setIsPublished] = useState(Boolean(sourcePackage?.is_published));
  const [contentReviewed, setContentReviewed] = useState(Boolean(
    sourcePackage?.packageWorkflow?.contentReview?.reviewed
    || sourcePackage?.package_workflow?.contentReview?.reviewed
  ));
  const [aiRequestMeta, setAiRequestMeta] = useState(
    sourcePackage?.aiRequestMeta || sourcePackage?.ai_request_meta || {}
  );
  const [generatedFields, setGeneratedFields] = useState<PackageAiGeneratedField[]>(
    Array.isArray((sourcePackage?.packageWorkflow?.contentReview?.generatedFields)
      || (sourcePackage?.package_workflow as any)?.contentReview?.generatedFields)
      ? (((sourcePackage?.packageWorkflow?.contentReview?.generatedFields)
        || (sourcePackage?.package_workflow as any)?.contentReview?.generatedFields) as PackageAiGeneratedField[])
      : []
  );
  const [isGenerating, setIsGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const defaultHotelStartDate = useMemo(() => getDatePlusDays(new Date().toISOString(), 30), []);
  const getSelectedHotelForPlace = useCallback((placeIndex: number) =>
    selectedHotels.find((entry) => entry.placeIndex === placeIndex)?.hotel, [selectedHotels]);
  const displayedTransferRows = useMemo(() => getDisplayedTransferRows(transferRows), [transferRows]);
  const transferTableRows = useMemo(() => buildTransferTableRows(transferRows), [transferRows]);
  const hotelPricingRows = useMemo(() => places.map((place, index) => {
    const selectedHotel = selectedHotels.find((entry) => entry.placeIndex === index)?.hotel;
    return {
      id: `hotel-${index}`,
      cityName: place.cityName,
      nights: Math.max(1, Number(place.nights || 1)),
      name: selectedHotel?.name || '',
      price: selectedHotel ? getHotelStoredPrice(selectedHotel) : 0,
    };
  }).filter((item) => item.name), [places, selectedHotels]);
  const hotelPricingTotal = useMemo(
    () => hotelPricingRows.reduce((sum, item) => sum + Math.max(0, Number(item.price || 0)), 0),
    [hotelPricingRows]
  );
  const transferPricingTotal = useMemo(
    () => {
      const enteredPrices = transferTableRows
        .map((row) => Math.max(0, Number(row.price || 0)))
        .filter((price) => price > 0);
      if (enteredPrices.length === 0) return 0;
      return Math.min(...enteredPrices);
    },
    [transferTableRows]
  );
  const activityPricingTotal = useMemo(
    () => activityPricingRows.reduce((sum, row) => sum + Math.max(0, Number(row.price || 0)), 0),
    [activityPricingRows]
  );
  const calculatedStartingPrice = useMemo(
    () => calculatePackageStartingPrice(baseStartingPrice, displayedTransferRows),
    [baseStartingPrice, displayedTransferRows]
  );
  const calculatedPackageTotal = useMemo(
    () => hotelPricingTotal + transferPricingTotal + activityPricingTotal + visaPrice + insurancePrice,
    [activityPricingTotal, hotelPricingTotal, insurancePrice, transferPricingTotal, visaPrice]
  );
  const effectivePackageTotal = useMemo(() => {
    const numericOverride = Number(priceOverride);
    return Number.isFinite(numericOverride) && numericOverride > 0 ? numericOverride : calculatedPackageTotal;
  }, [calculatedPackageTotal, priceOverride]);

  const transferValidationErrors = useMemo(() => {
    const messages: string[] = [];
    const groupedRows = new Map<string, PackageCabPricingRow[]>();

    displayedTransferRows
      .filter((row) => row.isActive)
      .forEach((row) => {
        const cabKey = row.cabType;
        groupedRows.set(cabKey, [...(groupedRows.get(cabKey) || []), row]);
      });

    groupedRows.forEach((rows, cabType) => {
      if (rows.length > 1) messages.push(`${cabType} has more than one active transfer row.`);
    });

    return messages;
  }, [displayedTransferRows]);

  const readinessBlockers = useMemo(() => {
    const blockers: string[] = [];
    if (generatedFields.length > 0 && !contentReviewed) {
      blockers.push('Review AI-generated content before publishing.');
    }
    if (!(Number(effectivePackageTotal || 0) > 0)) {
      blockers.push('Set a valid total price.');
    }
    if (!heroImageFile && !heroImagePreview) {
      blockers.push('Upload a hero image.');
    }
    const hasTransferPrice = transferTableRows.some((row) => Number(row.price || 0) > 0);
    if (!hasTransferPrice) {
      blockers.push('Add at least one cab price.');
    }
    if (transferValidationErrors.length > 0) {
      blockers.push(...transferValidationErrors);
    }
    if (!(role === 'admin' || permissions.canCreatePackages)) {
      blockers.push('You do not have publish permission.');
    }
    return blockers;
  }, [
    effectivePackageTotal,
    contentReviewed,
    transferTableRows,
    generatedFields.length,
    heroImageFile,
    heroImagePreview,
    permissions.canCreatePackages,
    role,
    transferValidationErrors,
  ]);

  const steps = ['Basics', 'Review & Edit', 'Pricing', 'Media & Publish'];

  const loadAirportOptions = useCallback(async (inputValue: string): Promise<LocationOption[]> => {
    try {
      return await loadAirportLocations(inputValue);
    } catch {
      return [];
    }
  }, []);

  const loadCityOptions = useCallback(async (inputValue: string): Promise<LocationOption[]> => {
    try {
      return await loadCityLocations(inputValue);
    } catch {
      return [];
    }
  }, []);

  const buildPayload = useCallback(() => {
    const normalizedPlaces = places
      .map((place) => ({
        cityName: place.cityName.trim(),
        cityCode: place.cityCode.trim() || undefined,
        nights: Math.max(1, Number(place.nights || 1)),
      }))
      .filter((place) => place.cityName);
    const normalizedHighlights = highlights.map((item) => item.trim()).filter(Boolean);
    const normalizedItinerary = itinerary
      .map((day, index) => ({
        day: index + 1,
        title: day.title.trim(),
        description: (day.description || day.points.join(' ')).trim(),
        points: day.points.map((point) => point.trim()).filter(Boolean),
      }))
      .filter((day) => day.title || day.description || day.points.length > 0);
    const normalizedFaq = faq
      .map((item) => ({
        question: item.question.trim(),
        answer: item.answer.trim(),
      }))
      .filter((item) => item.question && item.answer);
    const normalizedSearchTags = searchTags.map((item) => item.trim()).filter(Boolean);
    const normalizedTransfers = transferRows.map((row) => ({
      id: row.id,
      tempId: row.tempId,
      cabType: row.cabType,
      maxOccupancy: getCabMaxOccupancy(row.cabType),
      price: Math.max(0, Number(row.price || 0)),
      isActive: Number(row.price || 0) > 0,
      isDeleted: Boolean(row.isDeleted),
    }));
    const normalizedHotels = selectedHotels.map(({ placeIndex, hotel }) => ({
      ...hotel,
      placeIndex,
      city: places[placeIndex]?.cityName || hotel.city_name || '',
      city_code: places[placeIndex]?.cityCode || hotel.city_code || '',
    }));
    const normalizedActivities = activityPricingRows
      .map((row) => ({
        id: row.id,
        name: row.name.trim(),
        price: Math.max(0, Number(row.price || 0)),
        currency: 'INR',
      }))
      .filter((row) => row.name);
    const normalizedVisaDetails = visaPrice > 0
      ? [{ type: 'Visa', price: Math.max(0, Number(visaPrice || 0)), validity: '', documents: [], timeRequired: '', currency: 'INR' }]
      : [];
    const normalizedInsuranceDetails = insurancePrice > 0
      ? [{ name: 'Insurance', price: Math.max(0, Number(insurancePrice || 0)), currency: 'INR', description: '' }]
      : [];
    const transfersBreakdownTotal = normalizedTransfers
      .filter((row) => !row.isDeleted)
      .map((row) => Math.max(0, Number(row.price || 0)))
      .filter((price) => price > 0)
      .reduce((minimum, price) => (minimum === null ? price : Math.min(minimum, price)), null as number | null) || 0;
    const hotelsBreakdownTotal = normalizedHotels
      .reduce((sum, hotel: any) => sum + Math.max(0, Number(hotel?.price || hotel?.display_price || 0)), 0);
    const activitiesBreakdownTotal = normalizedActivities
      .reduce((sum, activity) => sum + Math.max(0, Number(activity.price || 0)), 0);
    const derivedDurationLabel = durationLabel.trim() || `${Math.max(1, normalizedPlaces.reduce((sum, place) => sum + place.nights, 0) + 1)} days`;
    const derivedRouteLabel = routeLabel.trim() || normalizedPlaces.map((place) => place.cityName).filter(Boolean).join(' + ');
    const normalizedDestinationSlugs = destinationSlugs.map(slugify).filter(Boolean);
    const normalizedStyleTags = normalizedSearchTags.map((item) => item.trim()).filter(Boolean);
    const normalizedBestFor = bestFor.map((item) => item.trim()).filter(Boolean);
    const normalizedStayStyle = stayStyle.trim();
    const packageConfig = {
      startingPrice: effectivePackageTotal,
      basePassengerCount: 2,
      defaultPassengers: 2,
      hotelOptions: normalizedHotels.map((hotel: any, index) => ({
        id: String(hotel.hotelId || hotel.id || `hotel-${index + 1}`),
        name: String(hotel.name || ''),
        description: String(hotel.description || hotel.address || ''),
        stars: String(hotel.rating || hotel.star_rating || ''),
        nights: `${Math.max(1, Number(places[Number(hotel.placeIndex || 0)]?.nights || 1))} night(s)`,
        priceAdjustment: Math.max(0, Number(hotel.price || hotel.display_price || 0)),
      })),
      hotelStops: normalizedHotels.map((hotel: any, index) => ({
        destinationId: slugify(places[Number(hotel.placeIndex || 0)]?.cityName || hotel.city || `stop-${index + 1}`),
        destinationName: places[Number(hotel.placeIndex || 0)]?.cityName || hotel.city || '',
        nights: `${Math.max(1, Number(places[Number(hotel.placeIndex || 0)]?.nights || 1))} night(s)`,
        selectedHotel: {
          id: String(hotel.hotelId || hotel.id || `hotel-${index + 1}`),
          name: String(hotel.name || ''),
          category: String(hotel.rating || hotel.star_rating || ''),
          location: String(hotel.address || hotel.location || ''),
          priceAdjustment: Math.max(0, Number(hotel.price || hotel.display_price || 0)),
        },
      })),
      cabOptions: normalizedTransfers
        .filter((row) => !row.isDeleted && row.price > 0)
        .map((row) => ({
          id: slugify(row.cabType),
          name: row.cabType,
          description: `${row.maxOccupancy} passenger max occupancy`,
          priceAdjustment: row.price,
          recommendedFor: `${row.maxOccupancy} traveler(s)`,
        })),
      highlights: normalizedHighlights,
      taxNote: 'Final quote may vary by travel date, room availability, and passenger mix.',
    };

    return {
      title: packageName.trim(),
      subtitle: subtitle.trim(),
      description: description.trim(),
      summary: description.trim(),
      durationLabel: derivedDurationLabel,
      routeLabel: derivedRouteLabel,
      destinationSlugs: normalizedDestinationSlugs,
      primaryDestinationSlug: slugify(primaryDestinationSlug) || normalizedDestinationSlugs[0] || '',
      styleTags: normalizedStyleTags,
      bestFor: normalizedBestFor,
      stayStyle: normalizedStayStyle,
      inclusions: PACKAGE_INCLUSIONS,
      exclusions: PACKAGE_EXCLUSIONS,
      includedExperiences: PACKAGE_INCLUSIONS,
      packageConfig,
      places: normalizedPlaces,
      cities: normalizedPlaces.map((place) => place.cityName),
      arrivalAt: arrivalAt.trim(),
      departureFrom: departureFrom.trim(),
      highlights: normalizedHighlights,
      itinerary: normalizedItinerary,
      faq: normalizedFaq,
      searchTags: normalizedSearchTags,
      durationNights: normalizedPlaces.reduce((sum, place) => sum + place.nights, 0),
      durationDays: Math.max(1, normalizedPlaces.reduce((sum, place) => sum + place.nights, 0) + 1),
      baseStartingPrice: Math.max(0, Number(baseStartingPrice || 0)),
      publicStartingPrice: Math.max(calculatedStartingPrice, effectivePackageTotal),
      packageCabPricing: normalizedTransfers,
      hotels: normalizedHotels,
      activities: normalizedActivities,
      visaDetails: normalizedVisaDetails,
      insuranceDetails: normalizedInsuranceDetails,
      packageWorkflow: {
        isDraft: !isPublished,
        contentReview: {
          isGenerated: generatedFields.length > 0,
          reviewed: contentReviewed,
          reviewedAt: contentReviewed ? new Date().toISOString() : undefined,
          generatedFields,
        },
        publishReadiness: {
          ready: readinessBlockers.length === 0,
          blockers: readinessBlockers,
          checkedAt: new Date().toISOString(),
        },
      },
      aiRequestMeta,
      isPublished,
      seo: {},
      websiteSeo: {
        title: seoTitle.trim(),
        description: seoDescription.trim(),
      },
      price: {
        base: effectivePackageTotal,
        per: 'per_person',
        currency: 'INR',
      },
      price_breakdown: {
        flights: 0,
        hotels: hotelsBreakdownTotal,
        activities: activitiesBreakdownTotal,
        transfers: transfersBreakdownTotal,
        addons: 0,
        visa: Math.max(0, Number(visaPrice || 0)),
        insurance: Math.max(0, Number(insurancePrice || 0)),
        subtotal: calculatedPackageTotal,
        total: effectivePackageTotal,
      },
    };
  }, [
    activityPricingRows,
    aiRequestMeta,
    baseStartingPrice,
    calculatedStartingPrice,
    calculatedPackageTotal,
    contentReviewed,
    description,
    effectivePackageTotal,
    generatedFields,
    highlights,
    insurancePrice,
    isPublished,
    itinerary,
    packageName,
    places,
    readinessBlockers,
    searchTags,
    subtitle,
    transferRows,
    selectedHotels,
    visaPrice,
    arrivalAt,
    bestFor,
    departureFrom,
    destinationSlugs,
    durationLabel,
    faq,
    primaryDestinationSlug,
    routeLabel,
    seoDescription,
    seoTitle,
    stayStyle,
  ]);

  const persistPackage = useCallback(async ({
    publish,
  }: {
    publish?: boolean;
  } = {}) => {
    const payload = buildPayload();
    if (!payload.title) {
      setError('Package name is required.');
      return;
    }
    if (payload.places.length === 0) {
      setError('Add at least one city and night split.');
      return;
    }
    if (publish && readinessBlockers.length > 0) {
      setError(readinessBlockers[0]);
      return;
    }

    setError('');
    setSaving(true);

    const formData = new FormData();
    formData.append('title', payload.title);
    if (payload.subtitle) formData.append('subtitle', payload.subtitle);
    if (payload.description) formData.append('description', payload.description);
    if ((payload as any).summary) formData.append('summary', (payload as any).summary);
    if ((payload as any).durationLabel) formData.append('durationLabel', (payload as any).durationLabel);
    if ((payload as any).routeLabel) formData.append('routeLabel', (payload as any).routeLabel);
    formData.append('destinationSlugs', JSON.stringify((payload as any).destinationSlugs || []));
    if ((payload as any).primaryDestinationSlug) {
      formData.append('primaryDestinationSlug', (payload as any).primaryDestinationSlug);
    }
    formData.append('styleTags', JSON.stringify((payload as any).styleTags || []));
    formData.append('bestFor', JSON.stringify((payload as any).bestFor || []));
    formData.append('includedExperiences', JSON.stringify((payload as any).includedExperiences || []));
    formData.append('inclusions', JSON.stringify((payload as any).inclusions || []));
    formData.append('exclusions', JSON.stringify((payload as any).exclusions || []));
    if ((payload as any).stayStyle) formData.append('stayStyle', (payload as any).stayStyle);
    formData.append('packageConfig', JSON.stringify((payload as any).packageConfig || {}));
    if (payload.arrivalAt) formData.append('arrivalAt', payload.arrivalAt);
    if (payload.departureFrom) formData.append('departureFrom', payload.departureFrom);
    formData.append('places', JSON.stringify(payload.places));
    formData.append('cities', JSON.stringify(payload.cities));
    formData.append('durationDays', String(payload.durationDays));
    formData.append('durationNights', String(payload.durationNights));
    formData.append('highlights', JSON.stringify(payload.highlights));
    formData.append('itinerary', JSON.stringify(payload.itinerary));
    formData.append('faq', JSON.stringify(payload.faq));
    formData.append('searchTags', JSON.stringify(payload.searchTags));
    formData.append('baseStartingPrice', String(payload.baseStartingPrice));
    formData.append('publicStartingPrice', String(payload.publicStartingPrice));
    formData.append('packageCabPricing', JSON.stringify(payload.packageCabPricing));
    formData.append('hotels', JSON.stringify(payload.hotels));
    formData.append('activities', JSON.stringify((payload as any).activities || []));
    formData.append('visaDetails', JSON.stringify((payload as any).visaDetails || []));
    formData.append('insuranceDetails', JSON.stringify((payload as any).insuranceDetails || []));
    formData.append('packageWorkflow', JSON.stringify(payload.packageWorkflow));
    formData.append('aiRequestMeta', JSON.stringify(payload.aiRequestMeta || {}));
    formData.append('price', JSON.stringify(payload.price));
    formData.append('price_breakdown', JSON.stringify(payload.price_breakdown));
    formData.append('seo', JSON.stringify((payload as any).websiteSeo || payload.seo));
    formData.append('isPublished', String(Boolean(publish ? true : payload.isPublished)));
    formData.append('flights', JSON.stringify([]));
    formData.append('transfers', JSON.stringify([]));
    formData.append('addons', JSON.stringify([]));
    formData.append('reviews', JSON.stringify([]));
    if (heroImageFile) formData.append('hero_image', heroImageFile);
    if (galleryFiles.length > 0) galleryFiles.forEach((file) => formData.append('gallery', file));

    try {
      const saved = editingId
        ? await updatePackage(editingId, formData)
        : await createPackage(formData);
      if (saved?.id) setEditingId(String(saved.id));
      if (publish) setIsPublished(true);
    } catch (saveError: any) {
      const message = saveError?.message || 'Failed to save package';
      setError(message);
    } finally {
      setSaving(false);
    }
  }, [buildPayload, editingId, galleryFiles, heroImageFile, readinessBlockers]);

  const handleGenerate = useCallback(async () => {
    if (!packageName.trim()) {
      setError('Package name is required before AI generation.');
      return;
    }
    const validPlaces = places
      .map((place) => ({
        cityName: place.cityName.trim(),
        cityCode: place.cityCode.trim() || undefined,
        nights: Math.max(1, Number(place.nights || 1)),
      }))
      .filter((place) => place.cityName);
    if (validPlaces.length === 0) {
      setError('Add at least one city before AI generation.');
      return;
    }
    setError('');
    setIsGenerating(true);
    try {
      const inferredDestinationSlugs = destinationSlugs.length > 0
        ? destinationSlugs
        : validPlaces.map((place) => slugify(place.cityName)).filter(Boolean);
      const generated = await generateWebsiteV2JourneyDraft({
        title: packageName.trim(),
        destinationSlugs: inferredDestinationSlugs,
        durationLabel: durationLabel.trim() || `${validPlaces.reduce((sum, place) => sum + place.nights, 0) + 1} days`,
        routeLabel: routeLabel.trim() || validPlaces.map((place) => place.cityName).join(' + '),
        styleTags: searchTags,
        roughDraft: roughDraft.trim() || undefined,
      });
      const draft = generated.draft || {};
      if (draft.title) setPackageName(draft.title);
      setSubtitle('');
      setDescription(draft.summary || '');
      setDurationLabel(draft.durationLabel || '');
      setRouteLabel(draft.routeLabel || '');
      setDestinationSlugs(Array.isArray(draft.destinationSlugs) ? draft.destinationSlugs.map(slugify).filter(Boolean) : inferredDestinationSlugs);
      setPrimaryDestinationSlug(draft.primaryDestinationSlug || inferredDestinationSlugs[0] || '');
      setBestFor(normalizeReviewTagArray(draft.bestFor));
      setHighlights(normalizeJourneyDraftHighlights(draft.highlights));
      setItinerary(Array.isArray(draft.itinerary) ? draft.itinerary.map((day, index) => normalizeItineraryDraftDay(day, index)) : []);
      setFaq(normalizePackageFaq(draft.faq));
      setSearchTags(normalizeReviewTagArray(draft.styleTags));
      setStayStyle(draft.stayStyle || '');
      setSeoTitle(draft.seo?.title || '');
      setSeoDescription(draft.seo?.description || '');
      setGeneratedFields((generated.generatedFields || AI_MANAGED_FIELDS) as PackageAiGeneratedField[]);
      setAiRequestMeta({
        requestId: generated.requestId,
        provider: generated.provider,
        model: generated.model,
        generatedFields: generated.generatedFields as PackageAiGeneratedField[] | undefined,
        generatedAt: new Date().toISOString(),
      });
      setContentReviewed(false);
      setStep(1);
    } catch (generationError: any) {
      setError(generationError?.message || 'Failed to generate package content');
    } finally {
      setIsGenerating(false);
    }
  }, [destinationSlugs, durationLabel, packageName, places, routeLabel, roughDraft, searchTags]);

  const updatePlace = (index: number, field: keyof BuilderPlace, value: string | number) => {
    setPlaces((current) => current.map((place, itemIndex) => {
      if (itemIndex !== index) return place;
      if (field === 'nights') {
        return { ...place, nights: Math.max(1, Number(value || 1)) };
      }
      return { ...place, [field]: String(value) };
    }));
  };

  const updateItineraryDay = (index: number, field: 'title' | 'description', value: string) => {
    setItinerary((current) => current.map((day, itemIndex) => {
      if (itemIndex !== index) return day;
      return { ...day, [field]: value };
    }));
  };

  const addDayPoint = (index: number) => {
    const draft = (dayPointDrafts[index] || '').trim();
    if (!draft) return;
    setItinerary((current) => current.map((day, itemIndex) => {
      if (itemIndex !== index) return day;
      return { ...day, points: Array.from(new Set([...(day.points || []), draft])) };
    }));
    setDayPointDrafts((current) => ({ ...current, [index]: '' }));
  };

  const removeDayPoint = (dayIndex: number, pointIndex: number) => {
    setItinerary((current) => current.map((day, itemIndex) => {
      if (itemIndex !== dayIndex) return day;
      return { ...day, points: day.points.filter((_, index) => index !== pointIndex) };
    }));
  };

  const updateFaq = (index: number, field: keyof BuilderFaq, value: string) => {
    setFaq((current) => current.map((item, itemIndex) => (
      itemIndex === index ? { ...item, [field]: value } : item
    )));
  };

  const updateTransferPriceByCabType = (cabType: PackageCabType, value: string) => {
    const nextPrice = Math.max(0, Number(value || 0));
    setTransferRows((current) => {
      const existingRow = current.find((row) => !row.isDeleted && row.cabType === cabType);
      if (existingRow) {
        return current.map((row) => {
          if ((row.id || row.tempId) !== (existingRow.id || existingRow.tempId)) return row;
          return {
            ...row,
            price: nextPrice,
            isActive: nextPrice > 0,
          };
        });
      }
      return [
        ...current,
        {
          ...buildEmptyTransferRow(cabType),
          price: nextPrice,
          isActive: nextPrice > 0,
        },
      ];
    });
  };

  const updateActivityPricingRow = (rowId: string, field: 'name' | 'price', value: string | number) => {
    setActivityPricingRows((current) => current.map((row) => (
      row.id === rowId
        ? {
          ...row,
          [field]: field === 'price' ? Math.max(0, Number(value || 0)) : String(value),
        }
        : row
    )));
  };

  const setPlaceOption = (index: number, option: LocationOption | null) => {
    setPlaces((current) => current.map((place, itemIndex) => (
      itemIndex === index
        ? {
          ...place,
          cityName: option?.label || '',
          cityCode: option?.value || '',
        }
        : place
    )));
  };

  const openHotelModal = (placeIndex: number) => {
    if (!places[placeIndex]?.cityName) {
      setError('Select a city before choosing a hotel.');
      return;
    }
    setError('');
    setHotelModalPlaceIndex(placeIndex);
  };

  const closeHotelModal = () => {
    setHotelModalPlaceIndex(null);
  };

  const handleSelectHotel = (hotel: Hotel) => {
    if (hotelModalPlaceIndex === null) return;
    const offsetDays = places
      .slice(0, hotelModalPlaceIndex)
      .reduce((sum, place) => sum + Math.max(0, Number(place.nights || 0)), 0);
    const placeCheckIn = getDatePlusDays(defaultHotelStartDate, offsetDays);
    const placeCheckOut = getDatePlusDays(placeCheckIn, Math.max(1, Number(places[hotelModalPlaceIndex]?.nights || 1)));
    const selectedNights = Math.max(1, Number(places[hotelModalPlaceIndex]?.nights || 1));
    const hotelPricing = calculateHotelPriceWithGst(Number(hotel.price || hotel.display_price || 0), selectedNights);
    const hotelWithDates: Hotel = {
      ...hotel,
      price: hotelPricing.totalPrice,
      display_price: hotelPricing.totalPrice,
      checkInDate: placeCheckIn,
      checkOutDate: placeCheckOut,
    };
    setSelectedHotels((current) => {
      const next = current.filter((entry) => entry.placeIndex !== hotelModalPlaceIndex);
      next.push({
        placeIndex: hotelModalPlaceIndex,
        hotel: {
          ...hotelWithDates,
          gstApplied: true,
          gstBasePrice: hotelPricing.basePrice,
          gstAmount: hotelPricing.gstAmount,
          gstRate: hotelPricing.gstRate,
        },
      });
      return next.sort((a, b) => a.placeIndex - b.placeIndex);
    });
    setHotelModalPlaceIndex(null);
  };

  const handleNext = async () => {
    if (step === 0) {
      await handleGenerate();
      return;
    }
    setStep((current) => Math.min(steps.length - 1, current + 1));
  };

  return (
    <Layout>
      <div className="guided-package-builder">
        <div className="guided-package-builder__header">
          <div>
            <h2>{editingId ? 'Edit Package' : 'New Package'}</h2>
          </div>
        </div>

        <div className="guided-package-builder__steps">
          {steps.map((label, index) => (
            <button
              key={label}
              type="button"
              className={`guided-package-builder__step ${step === index ? 'active' : ''}`}
              onClick={() => setStep(index)}
            >
              <span>{index + 1}</span>
              {label}
            </button>
          ))}
        </div>

        {error && <div className="package-editor-error">{error}</div>}

        {step === 0 && (
          <section className="guided-package-builder__panel">
            <h3>Basics</h3>
            <div className="guided-package-builder__grid">
              <label>
                <span>Package name</span>
                <input value={packageName} onChange={(e) => setPackageName(e.target.value)} className="package-editor-input" />
              </label>
              <label>
                <span>Arrival at</span>
                <AsyncSelect<LocationOption, false>
                  cacheOptions
                  defaultOptions
                  loadOptions={loadAirportOptions}
                  value={arrivalOption}
                  onChange={(option) => {
                    setArrivalOption(option);
                    setArrivalAt(option?.label || '');
                  }}
                  placeholder="Type or select an airport"
                  classNamePrefix="package-editor-select"
                />
              </label>
              <label>
                <span>Departure from</span>
                <AsyncSelect<LocationOption, false>
                  cacheOptions
                  defaultOptions
                  loadOptions={loadAirportOptions}
                  value={departureOption}
                  onChange={(option) => {
                    setDepartureOption(option);
                    setDepartureFrom(option?.label || '');
                  }}
                  placeholder="Type or select an airport"
                  classNamePrefix="package-editor-select"
                />
              </label>
            </div>
            <div className="guided-package-builder__section-head guided-package-builder__section-head--route">
              <div>
                <h4>Cities & Nights</h4>
                <div className="guided-package-builder__section-subtitle">Add the cities in your itinerary</div>
              </div>
              <button type="button" className="package-editor-btn-secondary" onClick={() => setPlaces((current) => [...current, { ...EMPTY_PLACE }])}>
                + Add city
              </button>
            </div>
            <div className="guided-package-builder__stack">
              {places.map((place, index) => (
                <div key={`place-${index}`} className="guided-package-builder__row-card">
                  <div className="guided-package-builder__route-step">
                    <div className="guided-package-builder__route-step-badge">{index + 1}</div>
                  </div>
                  <div className="guided-package-builder__route-fields">
                    <label>
                      <span>City</span>
                      <AsyncSelect<LocationOption, false>
                        cacheOptions
                        defaultOptions
                        loadOptions={loadCityOptions}
                        value={place.cityName ? { label: place.cityName, value: place.cityCode || place.cityName, type: 'city' } : null}
                        onChange={(option) => setPlaceOption(index, option)}
                        placeholder="Type or select a city"
                        classNamePrefix="package-editor-select"
                      />
                    </label>
                    <label>
                      <span>Nights</span>
                      <input
                        type="number"
                        min={1}
                        value={place.nights}
                        onChange={(e) => updatePlace(index, 'nights', e.target.value)}
                        className="package-editor-input"
                        placeholder="Nights"
                      />
                    </label>
                    <label>
                      <span>Hotel</span>
                      {(() => {
                        const selectedHotel = getSelectedHotelForPlace(index);
                        return selectedHotel ? (
                          <div className="guided-package-builder__hotel-card">
                            <div className="guided-package-builder__hotel-card-body">
                              <strong>{selectedHotel.name}</strong>
                              <div className="guided-package-builder__hotel-summary">
                                {(selectedHotel as any).city || (selectedHotel as any).city_name || place.cityName || 'Selected hotel'}
                              </div>
                            </div>
                            <button
                              type="button"
                              className="package-editor-btn-secondary"
                              onClick={() => openHotelModal(index)}
                            >
                              Change hotel
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="package-editor-btn-secondary guided-package-builder__hotel-select"
                            onClick={() => openHotelModal(index)}
                          >
                            Select hotel
                          </button>
                        );
                      })()}
                    </label>
                  </div>
                  <button
                    type="button"
                    className="guided-package-builder__icon-btn"
                    disabled={places.length === 1}
                    onClick={() => setPlaces((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                    aria-label="Delete city"
                    title="Delete city"
                  >
                    X
                  </button>
                </div>
              ))}
            </div>
            <label className="guided-package-builder__review-field guided-package-builder__rough-draft-field">
              <span>Rough draft for AI</span>
              <div className="guided-package-builder__rough-draft-wrap">
                <textarea
                  value={roughDraft}
                  onChange={(e) => setRoughDraft(e.target.value)}
                  className="package-editor-textarea guided-package-builder__textarea-sm"
                  placeholder="Optional notes for the AI draft, such as must-see spots, pacing, or a specific style."
                />
                <div className="guided-package-builder__field-hint">
                  Optional. Sent to the AI draft request as guidance.
                </div>
              </div>
            </label>
          </section>
        )}

        {step === 1 && (
          <section className="guided-package-builder__panel">
            <div className="guided-package-builder__review-header-card">
              <div className="guided-package-builder__review-header-title">
                <h3>Review & Edit Content</h3>
                {aiRequestMeta?.requestId && (
                  <div className="guided-package-builder__meta">
                    Last request: {aiRequestMeta.requestId}
                    {aiRequestMeta.model ? ` • ${aiRequestMeta.model}` : ''}
                    {aiRequestMeta.provider ? ` • ${aiRequestMeta.provider}` : ''}
                  </div>
                )}
              </div>
              <div className="guided-package-builder__header-actions">
                <button type="button" className="package-editor-btn-ghost guided-package-builder__review-regenerate" disabled={isGenerating} onClick={() => void handleGenerate()}>
                  {isGenerating ? 'Generating…' : 'Regenerate AI Content'}
                </button>
                <label className="guided-package-builder__checkbox guided-package-builder__review-checkbox">
                  <input type="checkbox" checked={contentReviewed} onChange={(e) => setContentReviewed(e.target.checked)} />
                  <span>Content reviewed</span>
                </label>
              </div>
            </div>
            <div className="guided-package-builder__review-card">
              <div className="guided-package-builder__review-grid guided-package-builder__review-grid--top">
                <label className="guided-package-builder__review-field">
                  <span>Website duration label</span>
                  <input
                    value={durationLabel}
                    onChange={(e) => setDurationLabel(e.target.value)}
                    className="package-editor-input"
                    placeholder="e.g. 7 days / 6 nights"
                  />
                </label>
                <label className="guided-package-builder__review-field">
                  <span>Website route label</span>
                  <input
                    value={routeLabel}
                    onChange={(e) => setRouteLabel(e.target.value)}
                    className="package-editor-input"
                    placeholder="e.g. Srinagar + Gulmarg + Pahalgam"
                  />
                </label>
                <ReviewTagField
                  label="Destination slugs"
                  value={destinationSlugs}
                  onChange={(next) => {
                    const normalized = next.map(slugify).filter(Boolean);
                    setDestinationSlugs(normalized);
                    setPrimaryDestinationSlug((current) => current || normalized[0] || '');
                  }}
                  placeholder="Add destination slug and press Enter"
                  tone="blue"
                  transform={slugify}
                />
                <label className="guided-package-builder__review-field">
                  <span>Primary destination slug</span>
                  <input
                    value={primaryDestinationSlug}
                    onChange={(e) => setPrimaryDestinationSlug(slugify(e.target.value))}
                    className="package-editor-input"
                    placeholder="e.g. kashmir"
                  />
                </label>
              </div>
              <label className="guided-package-builder__review-field">
                <span>Description</span>
                <div className="guided-package-builder__review-description">
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="package-editor-textarea guided-package-builder__textarea-lg"
                    maxLength={2000}
                  />
                  <div className="guided-package-builder__review-char-count">{description.length} / 2000</div>
                </div>
              </label>
              <div className="guided-package-builder__review-grid guided-package-builder__review-grid--middle">
                <ReviewTagField
                  label="Highlights"
                  value={highlights}
                  onChange={setHighlights}
                  placeholder="Add highlight and press Enter"
                  tone="gold"
                />
                <ReviewTagField
                  label="Search tags"
                  value={searchTags}
                  onChange={setSearchTags}
                  placeholder="Add search tag and press Enter"
                  tone="blue"
                />
                <ReviewTagField
                  label="Best for"
                  value={bestFor}
                  onChange={setBestFor}
                  placeholder="Add audience and press Enter"
                  tone="green"
                />
                <label className="guided-package-builder__review-field">
                  <span>Stay style</span>
                  <input
                    value={stayStyle}
                    onChange={(e) => setStayStyle(e.target.value)}
                  className="package-editor-input"
                  placeholder="e.g. Boutique beachfront resorts"
                />
              </label>
              </div>
            </div>
            <div className="guided-package-builder__section-head">
              <h4>Itinerary</h4>
              <button
                type="button"
                className="package-editor-btn-ghost"
                onClick={() => setItinerary((current) => [...current, { day: current.length + 1, title: '', points: [] }])}
              >
                + Add day
              </button>
            </div>
            <div className="guided-package-builder__stack">
              {itinerary.map((day, index) => (
                <div key={`itinerary-${index}`} className="guided-package-builder__itinerary-card">
                  <div className="guided-package-builder__itinerary-title">Day {index + 1}</div>
                  <input
                    value={day.title}
                    onChange={(e) => updateItineraryDay(index, 'title', e.target.value)}
                    className="package-editor-input"
                    placeholder="Day title"
                  />
                  <div className="guided-package-builder__points-block">
                    <div className="guided-package-builder__points-label">Day points</div>
                    <div className="package-editor-tag-input-container">
                      <input
                        value={dayPointDrafts[index] || ''}
                        onChange={(e) => setDayPointDrafts((current) => ({ ...current, [index]: e.target.value }))}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addDayPoint(index);
                          }
                        }}
                        className="package-editor-input"
                        placeholder="Add point and press Enter"
                      />
                      <button type="button" className="package-editor-btn-secondary" onClick={() => addDayPoint(index)}>
                        Add
                      </button>
                    </div>
                    {(day.points || []).length > 0 ? (
                      <ol className="guided-package-builder__points-list">
                        {(day.points || []).map((point, pointIndex) => (
                          <li key={`${point}-${pointIndex}`} className="guided-package-builder__point-item">
                            <span className="guided-package-builder__point-text">{point}</span>
                            <button
                              type="button"
                              className="guided-package-builder__point-remove"
                              onClick={() => removeDayPoint(index, pointIndex)}
                              aria-label="Delete point"
                              title="Delete point"
                            >
                              Remove
                            </button>
                          </li>
                        ))}
                      </ol>
                    ) : (
                      <div className="guided-package-builder__points-empty">No points added yet.</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="guided-package-builder__section-head">
              <h4>FAQ</h4>
              <button
                type="button"
                className="package-editor-btn-ghost"
                onClick={() => setFaq((current) => [...current, { question: '', answer: '' }])}
              >
                + Add FAQ
              </button>
            </div>
            <div className="guided-package-builder__stack">
              {faq.map((item, index) => (
                <div key={`faq-${index}`} className="guided-package-builder__faq-card">
                  <input
                    value={item.question}
                    onChange={(e) => updateFaq(index, 'question', e.target.value)}
                    className="package-editor-input"
                    placeholder="Question"
                  />
                  <textarea
                    value={item.answer}
                    onChange={(e) => updateFaq(index, 'answer', e.target.value)}
                    className="package-editor-textarea"
                    placeholder="Answer"
                  />
                </div>
              ))}
            </div>
            <div className="guided-package-builder__section-head">
              <h4>Website SEO</h4>
            </div>
            <div className="guided-package-builder__grid">
              <label>
                <span>SEO title</span>
                <input
                  value={seoTitle}
                  onChange={(e) => setSeoTitle(e.target.value)}
                  className="package-editor-input"
                />
              </label>
              <label>
                <span>SEO description</span>
                <input
                  value={seoDescription}
                  onChange={(e) => setSeoDescription(e.target.value)}
                  className="package-editor-input"
                />
              </label>
            </div>
          </section>
        )}

        {step === 2 && (
          <section className="guided-package-builder__panel">
            <h3>Pricing</h3>
            <div className="guided-package-builder__stack">
                <div className="guided-package-builder__pricing-section">
                  <div className="guided-package-builder__section-head">
                    <h4>Hotels</h4>
                  </div>
                  <div className="guided-package-builder__help">
                    Hotel GST is included at 5% up to INR 7,500 per room per night and 18% above that.
                  </div>
                  <div className="guided-package-builder__stack">
                    {hotelPricingRows.length === 0 ? (
                      <div className="guided-package-builder__empty-state">Select hotels in Basics to price them here.</div>
                    ) : hotelPricingRows.map((hotel) => (
                      <div key={hotel.id} className="guided-package-builder__pricing-row">
                      <div>
                        <strong>{hotel.name}</strong>
                        <div className="guided-package-builder__help">{hotel.nights} night{hotel.nights > 1 ? 's' : ''}</div>
                      </div>
                      <div className="guided-package-builder__pricing-row-price">INR {hotel.price.toLocaleString('en-IN')}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="guided-package-builder__pricing-section">
                <div className="guided-package-builder__section-head">
                  <h4>Transfers</h4>
                </div>
                <div className="guided-package-builder__table-wrap">
                  <table className="guided-package-builder__table">
                    <thead>
                      <tr>
                        <th>Cab type</th>
                        <th>Max occupancy</th>
                        <th>Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transferTableRows.map((row) => (
                        <tr key={row.cabType}>
                          <td>{row.cabType}</td>
                          <td>{row.maxOccupancy}</td>
                          <td>
                            <input
                              type="number"
                              min={0}
                              value={row.price}
                              onChange={(e) => updateTransferPriceByCabType(row.cabType, e.target.value)}
                              className="package-editor-input"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="guided-package-builder__help">
                  The lowest entered cab price is included in total pricing.
                </div>
              </div>
              <div className="guided-package-builder__pricing-section">
                <div className="guided-package-builder__section-head">
                  <h4>Activities</h4>
                  <button
                    type="button"
                    className="package-editor-btn-ghost"
                    onClick={() => setActivityPricingRows((current) => [...current, buildEmptyActivityPricingRow()])}
                  >
                    + Add activity
                  </button>
                </div>
                <div className="guided-package-builder__stack">
                  {activityPricingRows.map((row) => (
                    <div key={row.id} className="guided-package-builder__pricing-edit-row">
                      <input
                        value={row.name}
                        onChange={(e) => updateActivityPricingRow(row.id, 'name', e.target.value)}
                        className="package-editor-input"
                        placeholder="Activity name"
                      />
                      <input
                        type="number"
                        min={0}
                        value={row.price}
                        onChange={(e) => updateActivityPricingRow(row.id, 'price', e.target.value)}
                        className="package-editor-input"
                        placeholder="Price"
                      />
                      <button
                        type="button"
                        className="guided-package-builder__icon-btn"
                        onClick={() => setActivityPricingRows((current) => current.length > 1 ? current.filter((item) => item.id !== row.id) : [buildEmptyActivityPricingRow()])}
                        aria-label="Delete activity row"
                        title="Delete activity row"
                      >
                        🗑
                      </button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="guided-package-builder__pricing-section">
                <div className="guided-package-builder__section-head">
                  <h4>Other Components</h4>
                </div>
                <div className="guided-package-builder__grid">
                  <label>
                    <span>Visa price</span>
                    <input
                      type="number"
                      min={0}
                      value={visaPrice}
                      onChange={(e) => setVisaPrice(Math.max(0, Number(e.target.value || 0)))}
                      className="package-editor-input"
                    />
                  </label>
                  <label>
                    <span>Insurance price</span>
                    <input
                      type="number"
                      min={0}
                      value={insurancePrice}
                      onChange={(e) => setInsurancePrice(Math.max(0, Number(e.target.value || 0)))}
                      className="package-editor-input"
                    />
                  </label>
                </div>
              </div>
              <div className="guided-package-builder__price-summary">
                <span>Calculated total pricing</span>
                <strong>INR {calculatedPackageTotal.toLocaleString('en-IN')}</strong>
                <small>Hotels + transfers + activities + visa + insurance</small>
                <label>
                  <span>Override total price</span>
                  <input
                    type="number"
                    min={0}
                    value={priceOverride}
                    onChange={(e) => setPriceOverride(e.target.value === '' ? '' : Math.max(0, Number(e.target.value || 0)))}
                    className="package-editor-input"
                    placeholder="Optional override"
                  />
                </label>
                <div className="guided-package-builder__pricing-total">
                  Final total: <strong>INR {effectivePackageTotal.toLocaleString('en-IN')}</strong>
                </div>
                <div className="guided-package-builder__help">
                  Legacy starting price fields will be derived from this package pricing on save.
                </div>
              </div>
            </div>
          </section>
        )}

        {step === 3 && (
          <section className="guided-package-builder__panel">
            <h3>Media & Publish</h3>
            <div className="guided-package-builder__grid">
              <label>
                <span>Hero image</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    setHeroImageFile(file);
                    if (file) setHeroImagePreview(URL.createObjectURL(file));
                  }}
                  className="package-editor-input"
                />
              </label>
              <label>
                <span>Gallery</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => setGalleryFiles(Array.from(e.target.files || []))}
                  className="package-editor-input"
                />
              </label>
            </div>
            {heroImagePreview && (
              <div className="guided-package-builder__hero-preview">
                <img src={heroImagePreview} alt="Hero preview" />
              </div>
            )}
            <div className="guided-package-builder__checklist">
              <h4>Publish readiness</h4>
              <ul>
                {readinessBlockers.length === 0 ? (
                  <li>Ready to publish</li>
                ) : (
                  readinessBlockers.map((blocker) => <li key={blocker}>{blocker}</li>)
                )}
              </ul>
            </div>
            <label className="guided-package-builder__checkbox">
              <input type="checkbox" checked={isPublished} onChange={(e) => setIsPublished(e.target.checked)} />
              <span>Mark package as published on next save</span>
            </label>
          </section>
        )}

        <div className="guided-package-builder__footer">
          {step > 0 ? (
            <button type="button" className="package-editor-btn-ghost" onClick={() => setStep((current) => Math.max(0, current - 1))}>
              Back
            </button>
          ) : (
            <div aria-hidden="true" />
          )}
          {step === steps.length - 1 ? (
            <button
              type="button"
              className="package-editor-btn-primary"
              disabled={saving}
              onClick={() => void persistPackage({ publish: isPublished })}
            >
              {saving ? 'Saving…' : isPublished ? 'Save & Publish' : 'Save Package'}
            </button>
          ) : (
            <button type="button" className="package-editor-btn-primary" disabled={isGenerating} onClick={() => void handleNext()}>
              {step === 0 ? (isGenerating ? 'Generating…' : 'Next') : 'Next'}
            </button>
          )}
        </div>
        {hotelModalPlaceIndex !== null && (() => {
          const place = places[hotelModalPlaceIndex];
          const offsetDays = places
            .slice(0, hotelModalPlaceIndex)
            .reduce((sum, item) => sum + Math.max(0, Number(item.nights || 0)), 0);
          const checkIn = getDatePlusDays(defaultHotelStartDate, offsetDays);
          const checkOut = getDatePlusDays(checkIn, Math.max(1, Number(place?.nights || 1)));
          return (
            <HotelListModal
              place={{
                label: place.cityName,
                value: place.cityCode || place.cityName,
                nights: Math.max(1, Number(place.nights || 1)),
              }}
              checkIn={checkIn}
              checkOut={checkOut}
              travellers={DEFAULT_HOTEL_TRAVELLERS}
              onClose={closeHotelModal}
              onSelect={handleSelectHotel}
            />
          );
        })()}
      </div>
    </Layout>
  );
};

export default GuidedPackageBuilder;
